/* BergHOFF V5 — global.js
 * Progressive enhancement only: every feature below has a working no-JS baseline
 * (real links, <details>, normal form posts). No dependencies.
 */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const html = document.documentElement;
  const header = qs('[data-header]');
  const desktopNav = window.matchMedia('(min-width: 1200px)');

  /* ---------- Shared: live region + scroll lock ---------- */
  const live = document.createElement('div');
  live.className = 'visually-hidden';
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('role', 'status');
  document.body.appendChild(live);
  const announce = (msg) => {
    live.textContent = '';
    window.setTimeout(() => { live.textContent = msg || ''; }, 60);
  };

  const locks = new Set();
  const lockScroll = (key, state) => {
    if (state) locks.add(key); else locks.delete(key);
    html.classList.toggle('is-locked', locks.size > 0);
  };

  /* ---------- Header: scrolled state ---------- */
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 4);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- Desktop mega menu (disclosure pattern) ---------- */
  const nav = qs('[data-site-nav]');
  if (nav) {
    const items = qsa('[data-mega-item]', nav);
    const setOpen = (item, open) => {
      const btn = qs('[data-mega-toggle]', item);
      item.toggleAttribute('data-open', open);
      if (btn) btn.setAttribute('aria-expanded', String(open));
    };
    const closeAll = (except) => items.forEach((item) => { if (item !== except) setOpen(item, false); });

    items.forEach((item) => {
      const btn = qs('[data-mega-toggle]', item);
      if (btn) {
        btn.addEventListener('click', () => {
          const open = !item.hasAttribute('data-open');
          closeAll(item);
          setOpen(item, open);
          nav.classList.remove('is-suppressed');
        });
      }
      item.addEventListener('mouseenter', () => {
        nav.classList.remove('is-suppressed');
        closeAll(item);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) setOpen(item, false);
      });
    });

    nav.addEventListener('mouseleave', () => nav.classList.remove('is-suppressed'));
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const active = items.find((i) => i.hasAttribute('data-open') || i.matches(':hover'));
      if (!active) return;
      const hadFocus = active.contains(document.activeElement);
      setOpen(active, false);
      nav.classList.add('is-suppressed');
      if (hadFocus) qs('[data-mega-toggle]', active)?.focus();
    });
    document.addEventListener('click', (event) => {
      if (!nav.contains(event.target)) closeAll();
    });
  }

  /* ---------- Lazy panels: <template data-lazy> inside a <details> ---------- */
  // The mobile menu and the header country/language form are kept in <template> so the page DOM stays
  // small; they are parsed on the first touch, hover, focus or open of their <details>.
  const hydrateLazy = (root) => {
    if (!root) return;
    qsa('template[data-lazy]', root).forEach((tpl) => tpl.replaceWith(tpl.content.cloneNode(true)));
  };
  const hydrateFrom = (event) => {
    const summary = event.target.closest && event.target.closest('summary');
    if (summary && summary.parentElement) hydrateLazy(summary.parentElement);
  };
  document.addEventListener('pointerdown', hydrateFrom, true);
  document.addEventListener('pointerover', hydrateFrom, { capture: true, passive: true });
  document.addEventListener('focusin', hydrateFrom);
  document.addEventListener('toggle', (event) => {
    if (event.target.open) hydrateLazy(event.target);
  }, true);

  /* ---------- Mobile menu drawer (<details>) ---------- */
  const drawer = qs('[data-menu-drawer]');
  if (drawer) {
    const summary = drawer.querySelector(':scope > summary');
    const backgroundEls = () => [
      qs('#MainContent'),
      ...qsa('.shopify-section-group-footer-group'),
      ...qsa('.announcement-wrapper'),
    ].filter(Boolean);

    const setTop = () => {
      if (!header) return;
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      drawer.style.setProperty('--drawer-top', `${Math.round(bottom)}px`);
    };

    drawer.addEventListener('toggle', () => {
      const open = drawer.open;
      if (open) setTop();
      lockScroll('menu', open);
      backgroundEls().forEach((el) => { el.inert = open; });
      if (!open) qsa('[data-drawer-sub][open]', drawer).forEach((sub) => { sub.open = false; });
    });

    // Delegated (capture): the sub-menus are created when the drawer content is hydrated.
    drawer.addEventListener('toggle', (event) => {
      const sub = event.target;
      if (!sub.matches || !sub.matches('[data-drawer-sub]') || !sub.open) return;
      sub.scrollTop = 0;
      qsa('[data-drawer-sub][open]', drawer).forEach((other) => { if (other !== sub) other.open = false; });
    }, true);

    drawer.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !drawer.open) return;
      event.stopPropagation();
      const sub = qsa('[data-drawer-sub][open]', drawer).pop();
      if (sub) {
        sub.open = false;
        sub.querySelector(':scope > summary')?.focus();
      } else {
        drawer.open = false;
        summary?.focus();
      }
    });

    const closeOnDesktop = () => { if (desktopNav.matches && drawer.open) drawer.open = false; };
    desktopNav.addEventListener('change', closeOnDesktop);
    window.addEventListener('pageshow', () => {
      if (!drawer.open) {
        lockScroll('menu', false);
        backgroundEls().forEach((el) => { el.inert = false; });
      }
    });
  }

  /* ---------- Disclosures (localization, search): Escape + click outside ---------- */
  const popovers = () => qsa('details[data-disclosure][open], details[data-search-modal][open]');
  document.addEventListener('click', (event) => {
    popovers().forEach((d) => { if (!d.contains(event.target)) d.open = false; });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    popovers().forEach((d) => {
      const hadFocus = d.contains(document.activeElement);
      d.open = false;
      if (hadFocus) d.querySelector(':scope > summary')?.focus();
    });
  });

  /* ---------- Search modal + predictive search ---------- */
  const searchModal = qs('[data-search-modal]');
  if (searchModal) {
    const input = qs('input[type="search"]', searchModal);
    const results = qs('[data-predictive-results]', searchModal);
    const status = qs('[data-predictive-status]', searchModal);

    searchModal.addEventListener('toggle', () => {
      if (searchModal.open) window.requestAnimationFrame(() => input?.focus());
    });
    qs('[data-search-close]', searchModal)?.addEventListener('click', () => {
      searchModal.open = false;
      searchModal.querySelector(':scope > summary')?.focus();
    });

    if (input && results && input.hasAttribute('data-predictive-input') && theme.routes.predictiveSearch) {
      let timer;
      let controller;
      const clear = () => { results.innerHTML = ''; if (status) status.textContent = ''; };
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < 2) { controller?.abort(); clear(); return; }
        timer = window.setTimeout(async () => {
          controller?.abort();
          controller = new AbortController();
          if (status) status.textContent = theme.strings.searchLoading || '';
          const params = new URLSearchParams({
            q: term,
            section_id: 'predictive-search',
            'resources[type]': 'product,collection,article,query',
            'resources[limit]': '6',
            'resources[options][unavailable_products]': 'last',
          });
          try {
            const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
            if (!res.ok) throw new Error(String(res.status));
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const content = doc.querySelector('[data-predictive-content]');
            results.innerHTML = content ? content.innerHTML : '';
            if (status) status.textContent = content ? content.dataset.status || '' : '';
          } catch (err) {
            if (err.name !== 'AbortError') clear();
          }
        }, 220);
      });
    }
  }

  /* ---------- Free delivery in a converted currency (SE, DK) ---------- */
  // snippets/free-shipping-amount: a market that pays in another currency gets the store-currency
  // threshold, converted here with Shopify's own rate (the one checkout uses for the free-delivery
  // discount) and rounded up to a whole unit, so the shop never promises less than checkout gives.
  const fxRate = () => parseFloat(window.Shopify?.currency?.rate || '0');
  const fxCurrency = () => window.Shopify?.currency?.active || '';
  const fxThreshold = (base) => Math.ceil((base / 100) * fxRate()) * 100;
  const fxFormat = (cents, decimals) => {
    try {
      return new Intl.NumberFormat(html.lang || undefined, {
        style: 'currency', currency: fxCurrency(), minimumFractionDigits: 0, maximumFractionDigits: decimals ? 2 : 0,
      }).format(cents / 100);
    } catch (e) {
      return `${(cents / 100).toFixed(decimals ? 2 : 0)} ${fxCurrency()}`;
    }
  };
  const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fillFx = (root = document) => {
    if (!fxRate() || !fxCurrency()) return;
    qsa('[data-fs-fx]', root).forEach((el) => { el.textContent = fxFormat(fxThreshold(Number(el.dataset.fsFx))); });
    qsa('[data-fs-progress]', root).forEach((el) => {
      const threshold = fxThreshold(Number(el.dataset.fsBase));
      const total = Number(el.dataset.fsTotal) || 0;
      const remaining = threshold - total;
      const text = qs('[data-fs-text]', el);
      if (text) {
        text.innerHTML = remaining > 0
          ? el.dataset.fsRemaining.replace('__AMOUNT__', escapeHtml(fxFormat(remaining, remaining % 100 !== 0)))
          : escapeHtml(el.dataset.fsDone);
      }
      const bar = qs('.cart-progress__bar span', el);
      if (bar) bar.style.setProperty('--progress', `${Math.min(100, Math.round((total * 100) / threshold))}%`);
      el.classList.toggle('cart-progress--done', remaining <= 0);
      el.hidden = false;
    });
  };
  fillFx();

  // Product structured data in a converted currency (SE, DK): snippets/structured-data-shipping-fx gives
  // the store-currency rate; Google wants shippingRate in the offer's currency, so it is converted with
  // the same Shopify rate (0 when the product alone reaches the free delivery threshold).
  const addFxShipping = () => {
    const rate = fxRate();
    if (!rate || !fxCurrency()) return;
    qsa('script[type="application/ld+json"][data-ship-fx]').forEach((el) => {
      const [base, handling, transit, free] = el.dataset.shipFx.split(':');
      const days = (v) => v.split('-').map(Number);
      const [hMin, hMax] = days(handling);
      const [tMin, tMax] = days(transit);
      let data;
      try { data = JSON.parse(el.textContent); } catch (e) { return; }
      const offers = [];
      const collect = (node) => {
        if (!node || typeof node !== 'object') return;
        if (node['@type'] === 'Offer') offers.push(node);
        Object.values(node).forEach((v) => (Array.isArray(v) ? v.forEach(collect) : collect(v)));
      };
      collect(data);
      offers.forEach((offer) => {
        if (offer.shippingDetails || offer.priceCurrency !== fxCurrency()) return;
        const freeFrom = Number(free) > 0 ? fxThreshold(Number(free)) / 100 : Infinity;
        const value = Number(offer.price) >= freeFrom ? 0 : Math.round((Number(base) / 100) * rate * 100) / 100;
        offer.shippingDetails = {
          '@type': 'OfferShippingDetails',
          shippingRate: { '@type': 'MonetaryAmount', value, currency: offer.priceCurrency },
          shippingDestination: { '@type': 'DefinedRegion', addressCountry: el.dataset.shipCountry },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: { '@type': 'QuantitativeValue', minValue: hMin, maxValue: hMax, unitCode: 'DAY' },
            transitTime: { '@type': 'QuantitativeValue', minValue: tMin, maxValue: tMax, unitCode: 'DAY' },
          },
        };
      });
      el.textContent = JSON.stringify(data);
    });
  };
  addFxShipping();

  /* ---------- Current offers strip (sections/promo-strip) ---------- */
  // An offer whose end time passed while the page sat in Shopify's cache is removed; the code button copies.
  qsa('[data-promo-ends]').forEach((el) => { if (Number(el.dataset.promoEnds) * 1000 <= Date.now()) el.remove(); });
  qsa('.promo-strip').forEach((strip) => { if (!qs('.promo-strip__item', strip)) strip.closest('.shopify-section')?.remove(); });
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-copy-code]');
    if (!btn) return;
    const code = btn.dataset.copyCode;
    const done = () => {
      const label = qs('.promo-strip__code-text', btn);
      const status = qs('[data-copy-status]', btn.closest('.promo-strip__item, .product-price__offer') || document);
      btn.classList.add('is-copied');
      if (label) label.textContent = btn.dataset.copied || code;
      if (status) status.textContent = btn.dataset.copied || '';
      setTimeout(() => {
        btn.classList.remove('is-copied');
        if (label) label.textContent = code;
        if (status) status.textContent = '';
      }, 1800);
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(code).then(done, done);
    else done();
  });

  /* ---------- Cart ---------- */
  const cartDrawer = () => qs('[data-cart-drawer]');
  const cartRoots = () => qsa('[data-cart-root]');
  const sectionsToRender = () => cartRoots().map((root) => root.dataset.section).filter(Boolean);

  const updateCartCount = () => {
    const source = qs('[data-cart-count-source]');
    if (!source) return;
    const count = parseInt(source.textContent, 10) || 0;
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    if (theme.strings.cartLink) {
      qsa('[data-cart-link]').forEach((el) => el.setAttribute('aria-label', theme.strings.cartLink.replace('__COUNT__', count)));
    }
    updateVolume(count);
  };

  /* ---------- Volume discount ladder on the product page (snippets/volume-tiers) ---------- */
  // Counts the products in the cart plus the quantity about to be added, marks the tiers reached and says
  // what the next tier needs. The discount itself is applied by Shopify (automatic order discounts).
  function updateVolume(cartCount) {
    // Only products that count towards a tier (snippets/volume-eligible); the cart sections print that number.
    const source = qs('[data-volume-count-source]');
    let eligible = source ? parseInt(source.textContent, 10) || 0 : null;
    if (eligible === null && cartCount === 0) eligible = 0;
    qsa('[data-volume]').forEach((box) => {
      if (eligible !== null) box.dataset.volumeCount = String(eligible);
      let strings;
      try { strings = JSON.parse(qs('[data-volume-strings]', box).textContent); } catch (e) { return; }
      const tiers = box.dataset.volumeTiers.split(',').map(Number);
      const percents = box.dataset.volumePercents.split(',').map(Number);
      const qtyInput = qs('input[name="quantity"]', box.closest('section') || document);
      const qty = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
      const n = (parseInt(box.dataset.volumeCount, 10) || 0) + qty;
      let cur = -1;
      let next = -1;
      tiers.forEach((q, i) => {
        if (q <= n) cur = i;
        else if (next === -1) next = i;
      });
      qsa('[data-volume-step]', box).forEach((li, i) => {
        li.classList.toggle('is-reached', tiers[i] <= n);
        li.classList.toggle('is-current', i === cur);
      });
      const status = qs('[data-volume-status]', box);
      if (!status) return;
      const parts = [];
      if (cur !== -1 && next === -1) {
        parts.push(['', strings.max.replace('__PCT__', percents[cur])]);
      } else {
        if (cur !== -1) parts.push(['strong', strings.active.replace('__PCT__', percents[cur])]);
        const more = strings.next[String(tiers[next] - n)];
        if (more) parts.push(['', more.replace('__PCT__', percents[next])]);
      }
      const key = JSON.stringify(parts);
      if (status.dataset.volumeKey === key) return;
      status.dataset.volumeKey = key;
      status.replaceChildren(...parts.flatMap(([tag, text], i) => {
        const node = tag ? Object.assign(document.createElement(tag), { textContent: text }) : document.createTextNode(text);
        return i ? [document.createTextNode(' '), node] : [node];
      }));
    });
  }
  document.addEventListener('input', (event) => {
    if (event.target.matches?.('input[name="quantity"]') && event.target.closest('section')?.querySelector('[data-volume]')) updateVolume();
  });

  const renderSections = (sections) => {
    if (!sections) return;
    // Remember where focus was so keyboard users don't lose their place after re-render.
    const active = document.activeElement;
    const activeLine = active?.closest?.('[data-cart-line]')?.dataset.cartLine;
    const activeRole = active?.matches?.('[data-qty-step]') ? `[data-qty-step="${active.dataset.qtyStep}"]` : '[data-line-qty]';
    let focusLost = false;

    Object.entries(sections).forEach(([id, markup]) => {
      if (!markup) return;
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = doc.querySelector('[data-cart-root]');
      const current = cartRoots().find((root) => root.dataset.section === id);
      if (fresh && current) {
        if (current.contains(active)) focusLost = true;
        current.replaceWith(fresh);
      }
    });
    updateCartCount();
    fillFx();

    if (focusLost) {
      const scope = cartDrawer()?.open ? cartDrawer() : document;
      const target = (activeLine && qs(`[data-cart-line="${activeLine}"] ${activeRole}`, scope))
        || qs('#CartDrawerTitle', scope)
        || qs('[data-cart-root] h1, [data-cart-root] h2', scope);
      if (target) {
        if (!target.matches('a, button, input')) target.setAttribute('tabindex', '-1');
        target.focus();
      }
    }
  };

  const openCart = () => {
    const dialog = cartDrawer();
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) {
      dialog.showModal();
      lockScroll('cart', true);
    }
    return true;
  };
  const closeCart = () => cartDrawer()?.close();

  // Native <dialog> restores focus to the element that opened it.
  document.addEventListener('close', (event) => {
    if (event.target.matches?.('[data-cart-drawer]')) lockScroll('cart', false);
  }, true);

  document.addEventListener('click', (event) => {
    const dialog = cartDrawer();
    if (dialog && event.target === dialog) closeCart(); // backdrop click
  });

  const postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  // Add to cart (product page, product cards, sticky bar via form="…")
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\.js)?$/.test(action.split('?')[0])) return;
    if (theme.cartType !== 'drawer' || !cartDrawer()) return;

    event.preventDefault();
    const submitter = event.submitter || qs('[type="submit"]', form);
    const errorEl = qs('[data-form-error]', form) || qs(`[data-form-error-for="${form.id}"]`);
    if (submitter?.getAttribute('aria-disabled') === 'true') return;

    const body = new FormData(form);
    const sections = sectionsToRender();
    if (sections.length) {
      body.append('sections', sections.join(','));
      body.append('sections_url', window.location.pathname);
    }

    submitter?.setAttribute('aria-disabled', 'true');
    submitter?.classList.add('is-loading');
    if (errorEl) errorEl.hidden = true;

    try {
      const res = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
      });
      const data = await res.json();
      if (data.status) {
        const msg = data.description || data.message || theme.strings.cartError;
        if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
        announce(msg);
        return;
      }
      renderSections(data.sections);
      openCart();
      announce(theme.strings.added);
    } catch (err) {
      if (errorEl) { errorEl.textContent = theme.strings.cartError; errorEl.hidden = false; }
    } finally {
      submitter?.removeAttribute('aria-disabled');
      submitter?.classList.remove('is-loading');
    }
  });

  // Line item quantity / remove (drawer + cart page)
  const pending = new Map();
  const changeLine = (lineEl, quantity) => {
    const root = lineEl.closest('[data-cart-root]');
    const line = Number(lineEl.dataset.cartLine);
    window.clearTimeout(pending.get(line));
    pending.set(line, window.setTimeout(async () => {
      root?.classList.add('is-loading');
      try {
        const data = await postJSON(theme.routes.cartChange, {
          line,
          quantity,
          sections: sectionsToRender(),
          sections_url: window.location.pathname,
        });
        if (data.status) {
          const err = qs('[data-line-error]', lineEl);
          if (err) { err.textContent = data.description || data.message || theme.strings.cartError; err.hidden = false; }
          root?.classList.remove('is-loading');
          return;
        }
        renderSections(data.sections);
        if (theme.strings.cartUpdated) announce(theme.strings.cartUpdated);
      } catch (err) {
        root?.classList.remove('is-loading');
        const errorEl = qs('[data-line-error]', lineEl);
        if (errorEl) { errorEl.textContent = theme.strings.cartError; errorEl.hidden = false; }
      }
    }, 280));
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && openCart()) { event.preventDefault(); return; }

    if (event.target.closest('[data-cart-close]')) { closeCart(); return; }

    const step = event.target.closest('[data-qty-step]');
    if (step && step.closest('[data-cart-line]')) {
      const lineEl = step.closest('[data-cart-line]');
      const input = qs('[data-line-qty]', lineEl);
      const max = input.max ? Number(input.max) : Infinity;
      const next = Math.min(max, Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyStep)));
      input.value = String(next);
      changeLine(lineEl, next);
      return;
    }

    const remove = event.target.closest('[data-line-remove]');
    if (remove && remove.closest('[data-cart-root]')) {
      event.preventDefault();
      changeLine(remove.closest('[data-cart-line]'), 0);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-line-qty]');
    if (input && input.closest('[data-cart-root]')) {
      changeLine(input.closest('[data-cart-line]'), Math.max(0, parseInt(input.value, 10) || 0));
    }
  });

  /* ---------- "Read more" for clamped intros ---------- */
  qsa('[data-clamp]').forEach((el) => {
    const toggle = qs(`[data-clamp-toggle="${el.id}"]`);
    if (!toggle) return;
    const needsToggle = () => el.scrollHeight > el.clientHeight + 4;
    toggle.hidden = !needsToggle();
    toggle.addEventListener('click', () => {
      const expanded = el.classList.toggle('is-expanded');
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
    });
  });

  window.BergHOFF = Object.assign(window.BergHOFF || {}, { openCart, closeCart, announce, lockScroll });
})();
